// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO (notifications rework 2026-10, design/notifications-rework option A): the phone
// Notifications page as one short list. The master card (with "Send a test"), one card with
// every source that can notify (Agents opens its own page), then rows for what happens while
// Konvo is open, on this device and for replies; each opens a page of its own
// (PhoneNotificationsPage) so the root stays about a screen and a half long.

import {SettingsSection} from '@app/features/app/components/dialogs/shared/SettingsSection';
import {SettingsTabContainer, SettingsTabContent} from '@app/features/app/components/dialogs/shared/SettingsTabLayout';
import {ENABLE_NOTIFICATIONS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import Agents from '@app/features/konvo_agents/state/Agents';
import AgentsAccess from '@app/features/konvo_agents/state/AgentsAccess';
import {
	SOURCE_OFF_DESCRIPTOR,
	useNotificationSourceItems,
} from '@app/features/konvo_notifications/components/KonvoNotificationSources';
import {
	InAppPopupPreview,
	POPUP_MODE_DESCRIPTORS,
	PopupModeChoice,
	QUIET_CALLS_DESCRIPTOR,
	QUIET_CALLS_HINT_DESCRIPTOR,
	SHOW_DRAFT_DESCRIPTOR,
	SHOW_DRAFT_HINT_DESCRIPTOR,
	SHOW_POPUPS_FOR_DESCRIPTOR,
	SHOW_POPUPS_FOR_HINT_DESCRIPTOR,
	WAIT_TYPING_DESCRIPTOR,
	WAIT_TYPING_HINT_DESCRIPTOR,
} from '@app/features/konvo_popups/components/InAppPopupSettings';
import InAppPopupSettings from '@app/features/konvo_popups/state/InAppPopupSettings';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {
	AppWindowIcon,
	ArrowsClockwiseIcon,
	AtIcon,
	BellRingingIcon,
	BellSlashIcon,
	CaretRightIcon,
	CircleDotIcon,
	DesktopIcon,
	DeviceMobileIcon,
	InfoIcon,
	KeyboardIcon,
	MegaphoneIcon,
	PaperPlaneRightIcon,
	PhoneIcon,
	SparkleIcon,
	SpeakerHighIcon,
	TrashIcon,
} from '@app/features/ui/icons/Icons';
import NotificationState, {TTSNotificationMode} from '@app/features/ui/state/Notification';
import Sound from '@app/features/ui/state/Sound';
import {Tooltip} from '@app/features/ui/tooltip/Tooltip';
import {
	flagToMentionValue,
	MENTION_VALUE_DESCRIPTORS,
	MentionPreferenceTabContent,
} from '@app/features/user/components/modals/tabs/notifications_tab/MentionPreferenceTab';
import {
	agentsSummary,
	PhoneAgentsPage,
} from '@app/features/user/components/modals/tabs/notifications_tab/PhoneAgentsPage';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/PhoneNotifications.module.css';
import PhoneNotificationsPage, {
	type PhoneNotificationsSubpage,
} from '@app/features/user/components/modals/tabs/notifications_tab/PhoneNotificationsPage';
import {
	PhoneMessageSounds,
	PhoneSoundLevels,
} from '@app/features/user/components/modals/tabs/notifications_tab/PhoneSounds';
import {
	TextToSpeechNarrationCards,
	TextToSpeechPlaybackRow,
} from '@app/features/user/components/modals/tabs/notifications_tab/TextToSpeech';
import {
	shortSubscriptionId,
	summariseUserAgent,
} from '@app/features/user/components/modals/tabs/notifications_tab/UserAgentSummary';
import {useNotificationSettings} from '@app/features/user/components/modals/tabs/notifications_tab/useNotificationSettings';
import {useSoundSettings} from '@app/features/user/components/modals/tabs/notifications_tab/useSoundSettings';
import {useSettingsContentKey} from '@app/features/user/hooks/useSettingsContentKey';
import UserSettings from '@app/features/user/state/UserSettings';
import Users from '@app/features/user/state/Users';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {IconTerminal2} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {Fragment, useEffect, useId, useState} from 'react';

const SOURCES_ON_COUNT_DESCRIPTOR = msg({
	message: '{on} of {total} on',
	comment: 'Phone Settings > Notifications > Sources: how many sources may notify, e.g. "3 of 4 on".',
});
const NOTIFICATIONS_ON_DESCRIPTOR = msg({
	message: 'Notifications on',
	comment: 'Phone Settings > Notifications: master card title while notifications are enabled.',
});
const NOTIFICATIONS_OFF_DESCRIPTOR = msg({
	message: 'Notifications off',
	comment: 'Phone Settings > Notifications: master card title while notifications are disabled.',
});
const ALLOWED_ON_THIS_DEVICE_NAMED_DESCRIPTOR = msg({
	message: 'Allowed on this {device}',
	comment: 'Phone Settings > Notifications: the system allows notifications. {device} is e.g. "iPhone"; keep it.',
});
const ALLOWED_ON_THIS_DEVICE_DESCRIPTOR = msg({
	message: 'Allowed on this device',
	comment: 'Phone Settings > Notifications: the system allows notifications on this device.',
});
const BLOCKED_IN_DEVICE_SETTINGS_DESCRIPTOR = msg({
	message: 'Blocked in your device settings',
	comment: 'Phone Settings > Notifications: the system blocks notifications for Konvo.',
});
const NOT_ALLOWED_YET_DESCRIPTOR = msg({
	message: 'Not allowed yet',
	comment: 'Phone Settings > Notifications: the system has not been asked for permission yet.',
});
const NOT_AVAILABLE_HERE_DESCRIPTOR = msg({
	message: 'Not available in this browser',
	comment: 'Phone Settings > Notifications: the browser has no notification support.',
});
const PER_CHAT_HINT_DESCRIPTOR = msg({
	message: 'For a single chat or server, use Notification settings in its menu.',
	comment: 'Phone Settings > Notifications: hint under the master switch pointing to per-chat and per-server settings.',
});
const UNREAD_BADGE_DESCRIPTOR = msg({
	message: 'Unread badge',
	comment: 'Phone Settings > Notifications > General: switch for the unread count on the app icon.',
});
const UNREAD_BADGE_HINT_DESCRIPTOR = msg({
	message: 'Count on the app icon',
	comment: 'Phone Settings > Notifications > General: one line under "Unread badge".',
});
const PUSH_WHILE_ON_DESKTOP_DESCRIPTOR = msg({
	message: "Push while I'm on desktop",
	comment: 'Phone Settings > Notifications > General: phones get pushes even while the desktop app is in use.',
});
const PUSH_RIGHT_AWAY_DESCRIPTOR = msg({
	message: 'Your phone gets pushes right away. Muted chats stay quiet.',
	comment: 'Phone Settings > Notifications > General: under "Push while I\'m on desktop" when it is on.',
});
const PUSH_AFTER_IDLE_DESCRIPTOR = msg({
	message: 'Your phone waits until the desktop has been idle for',
	comment:
		'Phone Settings > Notifications > General: under "Push while I\'m on desktop" when it is off; followed by chips like "5 min".',
});
const IDLE_MINUTES_DESCRIPTOR = msg({
	message: '{minutes} min',
	comment: 'Phone Settings > Notifications > General: idle time chip, e.g. "5 min". Preserve {minutes}.',
});
const UNKNOWN_DEVICE_DESCRIPTOR = msg({
	message: 'Unknown device',
	comment: 'Phone Settings > Notifications > This device: a subscription whose device cannot be told.',
});
const PUSH_ACTIVE_DESCRIPTOR = msg({
	message: 'Push active',
	comment: 'Phone Settings > Notifications > This device: the device is registered for push.',
});
const PUSH_NOT_SET_UP_DESCRIPTOR = msg({
	message: 'Push not set up',
	comment: 'Phone Settings > Notifications > This device: the device has no push subscription.',
});
const CHECKING_DESCRIPTOR = msg({
	message: 'Checking…',
	comment: 'Phone Settings > Notifications > This device: loading the push subscriptions.',
});
const REFRESH_PUSH_DESCRIPTOR = msg({
	message: 'Refresh push subscription',
	comment: 'Tooltip of the refresh icon button on the phone push device card.',
});
const FORGET_SUBSCRIPTIONS_DESCRIPTOR = msg({
	message: 'Forget subscriptions',
	comment: 'Tooltip of the trash icon button on the phone push device card.',
});
const ENABLE_PUSH_DESCRIPTOR = msg({
	message: 'Enable push',
	comment: 'Phone Settings > Notifications > This device: button that registers this device for push.',
});
const SYSTEM_DESCRIPTOR = msg({
	message: 'System',
	comment: 'Phone Settings > Notifications > This device: label for the operating system, e.g. "iOS 26.6".',
});
const INSTALLED_APP_DESCRIPTOR = msg({
	message: 'installed app',
	comment: 'Phone Settings > Notifications > This device: after the OS when Konvo runs from the home screen.',
});
const READ_AUTOMATICALLY_DESCRIPTOR = msg({
	message: 'Read new messages automatically',
	comment: 'Phone Settings > Notifications: section title above the automatic narration choice cards.',
});

const NOTIFY_ME_ABOUT_DESCRIPTOR = msg({
	message: 'Notify me about',
	comment:
		'Phone Settings > Notifications: section title above the list of sources that can notify (Konvo chats, Teams, WhatsApp, mail, ClickUp, Agents).',
});
const WHILE_KONVO_IS_OPEN_DESCRIPTOR = msg({
	message: 'While Konvo is open',
	comment: 'Phone Settings > Notifications: section title above pop-ups, sounds and read aloud.',
});
const ON_THIS_DEVICE_NAMED_DESCRIPTOR = msg({
	message: 'On this {device}',
	comment:
		'Phone Settings > Notifications: section title for settings of this phone. {device} is e.g. "iPhone"; keep it.',
});
const ON_THIS_DEVICE_DESCRIPTOR = msg({
	message: 'On this device',
	comment: 'Phone Settings > Notifications: section title for settings of this phone, when its kind is unknown.',
});
const REPLIES_DESCRIPTOR = msg({
	message: 'Replies',
	comment: 'Phone Settings > Notifications: section title above the reply @mention row.',
});
const SEND_TEST_DESCRIPTOR = msg({
	message: 'Send a test',
	comment: 'Phone Settings > Notifications: button that sends one test push notification to every device with push on.',
});
const POPUPS_DESCRIPTOR = msg({
	message: 'Pop-ups',
	comment: 'Phone Settings > Notifications: row that opens the in-app pop-up settings.',
});
const SOUNDS_ROW_DESCRIPTOR = msg({
	message: 'Sounds',
	comment: 'Phone Settings > Notifications: row that opens the sound settings.',
});
const READ_ALOUD_ROW_DESCRIPTOR = msg({
	message: 'Read aloud',
	comment: 'Phone Settings > Notifications: row that opens the text-to-speech settings.',
});
const OFF_VALUE_DESCRIPTOR = msg({
	message: 'Off',
	comment: 'Phone Settings > Notifications: the value shown on a row whose feature is off (sounds, read aloud).',
});
const VOLUME_VALUE_DESCRIPTOR = msg({
	message: '{volume}%',
	comment:
		'Phone Settings > Notifications: the value on the Sounds row, the master volume, e.g. "100%". Keep {volume}.',
});
const EVERY_CHAT_VALUE_DESCRIPTOR = msg({
	message: 'Every channel',
	comment: 'Phone Settings > Notifications: value on the Read aloud row when every incoming message is read aloud.',
});
const OPEN_CHAT_VALUE_DESCRIPTOR = msg({
	message: 'Active channel',
	comment: 'Phone Settings > Notifications: value on the Read aloud row when only the open channel is read aloud.',
});
const AT_MY_DESK_DESCRIPTOR = msg({
	message: "While I'm at my desk",
	comment: 'Phone Settings > Notifications: row that opens when the phone gets pushes while the desktop app is in use.',
});
const AT_MY_DESK_HINT_DESCRIPTOR = msg({
	message: 'Pushes while the desktop app is in use',
	comment: 'Phone Settings > Notifications: one line under "While I\'m at my desk".',
});
const RIGHT_AWAY_DESCRIPTOR = msg({
	message: 'Right away',
	comment: 'Phone Settings > Notifications: value of "While I\'m at my desk" when the phone gets pushes immediately.',
});
const AFTER_MINUTES_DESCRIPTOR = msg({
	message: 'After {minutes} min',
	comment:
		'Phone Settings > Notifications: value of "While I\'m at my desk", pushes wait until the desktop is idle this long. Keep {minutes}.',
});
const MENTION_ROW_DESCRIPTOR = msg({
	message: 'Mention when I reply',
	comment: 'Phone Settings > Notifications: row that opens the reply @mention preference.',
});
const PUSH_ROW_DESCRIPTOR = msg({
	message: 'Push on this device',
	comment: "Phone Settings > Notifications: row that opens this phone's push subscription.",
});
const AGENTS_ROW_DESCRIPTOR = msg({
	message: 'Agents',
	comment: 'Phone Settings > Notifications > Notify me about: the coding agents row; opens its own page.',
});

/** Idle-time chips; the stored value is 1–10 minutes, so an odd one joins the row. */
const IDLE_CHIPS = [1, 2, 5, 10];

function thisDevice() {
	return summariseUserAgent(typeof navigator === 'undefined' ? null : navigator.userAgent);
}

function permissionState(): NotificationPermission | 'unsupported' {
	return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

const MasterCard: React.FC<{onToggle: (value: boolean) => void; onTest: () => Promise<void>}> = observer(
	({onToggle, onTest}) => {
		const {i18n} = useLingui();
		const [sending, setSending] = useState(false);
		const enabled = NotificationState.browserNotificationsEnabled;
		const permission = permissionState();
		const device = thisDevice().device;
		const status =
			permission === 'granted'
				? device
					? i18n._(ALLOWED_ON_THIS_DEVICE_NAMED_DESCRIPTOR, {device})
					: i18n._(ALLOWED_ON_THIS_DEVICE_DESCRIPTOR)
				: permission === 'denied'
					? i18n._(BLOCKED_IN_DEVICE_SETTINGS_DESCRIPTOR)
					: permission === 'default'
						? i18n._(NOT_ALLOWED_YET_DESCRIPTOR)
						: i18n._(NOT_AVAILABLE_HERE_DESCRIPTOR);
		const sendTest = () => {
			setSending(true);
			void onTest().finally(() => setSending(false));
		};
		return (
			<div className={clsx(styles.master, !enabled && styles.masterOff)} data-flx="user.notifications-tab.phone.master">
				<div className={styles.masterTop}>
					<span className={styles.masterTile} aria-hidden="true">
						{enabled ? <BellRingingIcon size={24} /> : <BellSlashIcon size={24} />}
					</span>
					<span className={styles.text}>
						<span className={styles.masterTitle}>
							{i18n._(enabled ? NOTIFICATIONS_ON_DESCRIPTOR : NOTIFICATIONS_OFF_DESCRIPTOR)}
						</span>
						<span className={styles.status}>
							<i
								className={clsx(
									styles.dot,
									permission === 'granted' && styles.dotOk,
									permission === 'denied' && styles.dotBad,
								)}
							/>
							{status}
						</span>
					</span>
					<Switch
						compact
						value={enabled}
						onChange={onToggle}
						ariaLabel={i18n._(ENABLE_NOTIFICATIONS_DESCRIPTOR)}
						data-flx="user.notifications-tab.phone.master-switch"
					/>
				</div>
				{enabled && (
					<div className={styles.actions}>
						<button
							type="button"
							className={styles.actionButton}
							onClick={sendTest}
							disabled={sending}
							data-flx="user.notifications-tab.phone.send-test"
						>
							<PaperPlaneRightIcon size={16} />
							{i18n._(SEND_TEST_DESCRIPTOR)}
						</button>
					</div>
				)}
			</div>
		);
	},
);

const SwitchRow: React.FC<{
	icon: React.ReactNode;
	title: string;
	description?: string;
	value: boolean;
	onChange: (value: boolean) => void;
	off?: boolean;
	logo?: boolean;
	'data-flx': string;
}> = ({icon, title, description, value, onChange, off, logo, 'data-flx': dataFlx}) => {
	const labelId = useId();
	return (
		<div className={clsx(styles.row, off && styles.rowOff)} data-flx={dataFlx}>
			<span className={logo ? styles.logoTile : styles.tile} aria-hidden="true">
				{icon}
			</span>
			<span className={styles.text}>
				<span id={labelId} className={styles.title}>
					{title}
				</span>
				{description ? <span className={styles.description}>{description}</span> : null}
			</span>
			<Switch compact value={value} onChange={onChange} ariaLabelledBy={labelId} data-flx={`${dataFlx}.switch`} />
		</div>
	);
};

/** A row that opens one of the tab's own pages. */
const NavRow: React.FC<{
	icon: React.ReactNode;
	title: string;
	description?: string;
	value?: string;
	page: PhoneNotificationsSubpage;
	logo?: boolean;
	'data-flx': string;
}> = ({icon, title, description, value, page, logo, 'data-flx': dataFlx}) => (
	<button
		type="button"
		className={clsx(styles.row, styles.navRow)}
		onClick={() => PhoneNotificationsPage.open(page)}
		data-flx={dataFlx}
	>
		<span className={logo ? styles.logoTile : styles.tile} aria-hidden="true">
			{icon}
		</span>
		<span className={styles.text}>
			<span className={styles.title}>{title}</span>
			{description ? <span className={styles.description}>{description}</span> : null}
		</span>
		<span className={styles.value}>
			{value ? <span>{value}</span> : null}
			<CaretRightIcon size={16} />
		</span>
	</button>
);

const SourcesSection: React.FC = observer(() => {
	const {i18n} = useLingui();
	const sources = useNotificationSourceItems(22);
	const agents = AgentsAccess.enabled;
	useEffect(() => {
		if (agents) void Agents.loadPrefs();
	}, [agents]);
	const on = sources.filter((source) => source.value).length + (agents ? 1 : 0);
	const total = sources.length + (agents ? 1 : 0);
	return (
		<SettingsSection
			id="notification-sources"
			title={i18n._(NOTIFY_ME_ABOUT_DESCRIPTOR)}
			actions={<span className={styles.sectionCount}>{i18n._(SOURCES_ON_COUNT_DESCRIPTOR, {on, total})}</span>}
			className={styles.bare}
		>
			<div>
				<div className={styles.card} data-flx="konvo-notifications.sources.list">
					{sources.map((source) => (
						<SwitchRow
							key={source.key}
							logo={true}
							icon={source.logo}
							title={source.label}
							description={source.value ? source.hint : i18n._(SOURCE_OFF_DESCRIPTOR)}
							value={source.value}
							onChange={source.onChange}
							off={!source.value}
							data-flx={`konvo-notifications.sources.${source.key}`}
						/>
					))}
					{agents && (
						<NavRow
							page="agents"
							logo={true}
							icon={<IconTerminal2 size={20} stroke={1.75} />}
							title={i18n._(AGENTS_ROW_DESCRIPTOR)}
							description={agentsSummary(i18n, Agents.prefs)}
							data-flx="konvo-notifications.sources.agents"
						/>
					)}
				</div>
				<p className={styles.foot}>
					<InfoIcon size={15} />
					<span>{i18n._(PER_CHAT_HINT_DESCRIPTOR)}</span>
				</p>
			</div>
		</SettingsSection>
	);
});

function readAloudValue(mode: TTSNotificationMode): MessageDescriptorLike {
	switch (mode) {
		case TTSNotificationMode.FOR_ALL_CHANNELS:
			return EVERY_CHAT_VALUE_DESCRIPTOR;
		case TTSNotificationMode.FOR_CURRENT_CHANNEL:
			return OPEN_CHAT_VALUE_DESCRIPTOR;
		default:
			return OFF_VALUE_DESCRIPTOR;
	}
}
type MessageDescriptorLike = typeof OFF_VALUE_DESCRIPTOR;

const RootPage: React.FC<{
	notifications: ReturnType<typeof useNotificationSettings>;
}> = observer(({notifications}) => {
	const {i18n} = useLingui();
	const device = thisDevice().device;
	const idleMinutes = Math.max(1, Math.min(10, Math.round(UserSettings.afkTimeout / 60)));
	const mention = flagToMentionValue(Users.getCurrentUser()?.mentionFlags ?? 0);
	const pushActive = notifications.subscriptions.length > 0;
	return (
		<>
			<MasterCard onToggle={notifications.toggleNotifications} onTest={notifications.sendTestNotification} />
			<SourcesSection />
			<SettingsSection id="in-app-popups" title={i18n._(WHILE_KONVO_IS_OPEN_DESCRIPTOR)} className={styles.bare}>
				<div className={styles.card}>
					<NavRow
						page="popups"
						icon={<AppWindowIcon size={20} />}
						title={i18n._(POPUPS_DESCRIPTOR)}
						value={i18n._(POPUP_MODE_DESCRIPTORS[InAppPopupSettings.mode])}
						data-flx="user.notifications-tab.phone.popups"
					/>
					<NavRow
						page="sounds"
						icon={<SpeakerHighIcon size={20} />}
						title={i18n._(SOUNDS_ROW_DESCRIPTOR)}
						value={
							Sound.settings.allSoundsDisabled
								? i18n._(OFF_VALUE_DESCRIPTOR)
								: i18n._(VOLUME_VALUE_DESCRIPTOR, {volume: Math.round(Sound.settings.masterVolume)})
						}
						data-flx="user.notifications-tab.phone.sounds"
					/>
					<NavRow
						page="read-aloud"
						icon={<MegaphoneIcon size={20} />}
						title={i18n._(READ_ALOUD_ROW_DESCRIPTOR)}
						value={i18n._(readAloudValue(NotificationState.getTTSNotificationMode()))}
						data-flx="user.notifications-tab.phone.read-aloud"
					/>
				</div>
			</SettingsSection>
			<SettingsSection
				id="notifications"
				title={device ? i18n._(ON_THIS_DEVICE_NAMED_DESCRIPTOR, {device}) : i18n._(ON_THIS_DEVICE_DESCRIPTOR)}
				className={styles.bare}
			>
				<div className={styles.card}>
					<SwitchRow
						icon={<CircleDotIcon size={20} />}
						title={i18n._(UNREAD_BADGE_DESCRIPTOR)}
						description={i18n._(UNREAD_BADGE_HINT_DESCRIPTOR)}
						value={NotificationState.unreadMessageBadgeEnabled}
						onChange={notifications.toggleUnreadBadge}
						data-flx="user.notifications-tab.phone.unread-badge"
					/>
					<NavRow
						page="desk"
						icon={<DesktopIcon size={20} />}
						title={i18n._(AT_MY_DESK_DESCRIPTOR)}
						description={i18n._(AT_MY_DESK_HINT_DESCRIPTOR)}
						value={
							UserSettings.afkTimeout === 0
								? i18n._(RIGHT_AWAY_DESCRIPTOR)
								: i18n._(AFTER_MINUTES_DESCRIPTOR, {minutes: idleMinutes})
						}
						data-flx="user.notifications-tab.phone.desk"
					/>
					{notifications.isPwaMobile && (
						<NavRow
							page="device"
							icon={<DeviceMobileIcon size={20} />}
							title={i18n._(PUSH_ROW_DESCRIPTOR)}
							value={i18n._(
								notifications.subscriptionsLoading
									? CHECKING_DESCRIPTOR
									: pushActive
										? PUSH_ACTIVE_DESCRIPTOR
										: PUSH_NOT_SET_UP_DESCRIPTOR,
							)}
							data-flx="user.notifications-tab.phone.device"
						/>
					)}
				</div>
			</SettingsSection>
			<SettingsSection id="mention-preference" title={i18n._(REPLIES_DESCRIPTOR)} className={styles.bare}>
				<div className={styles.card}>
					<NavRow
						page="mention"
						icon={<AtIcon size={20} />}
						title={i18n._(MENTION_ROW_DESCRIPTOR)}
						value={i18n._(MENTION_VALUE_DESCRIPTORS[mention])}
						data-flx="user.notifications-tab.phone.mention"
					/>
				</div>
			</SettingsSection>
		</>
	);
});

const PopupsPage: React.FC = observer(() => {
	const {i18n} = useLingui();
	const off = InAppPopupSettings.mode === 'off';
	return (
		<>
			<div className={styles.previewStage} aria-hidden="true">
				<InAppPopupPreview />
			</div>
			<SettingsSection
				id="popups-mode"
				linkable={false}
				title={i18n._(SHOW_POPUPS_FOR_DESCRIPTOR)}
				className={styles.bare}
			>
				<div>
					<div className={styles.card}>
						<div className={styles.modeCard}>
							<PopupModeChoice compact={true} grow={true} />
						</div>
					</div>
					<p className={styles.foot}>{i18n._(SHOW_POPUPS_FOR_HINT_DESCRIPTOR)}</p>
				</div>
			</SettingsSection>
			{!off && (
				<SettingsSection
					id="popups-behaviour"
					linkable={false}
					title={i18n._(POPUPS_DESCRIPTOR)}
					className={styles.bare}
				>
					<div className={styles.card} data-flx="user.notifications-tab.phone.popups-card">
						<SwitchRow
							icon={<SparkleIcon size={20} />}
							title={i18n._(SHOW_DRAFT_DESCRIPTOR)}
							description={i18n._(SHOW_DRAFT_HINT_DESCRIPTOR)}
							value={InAppPopupSettings.showDraft}
							onChange={(showDraft) => InAppPopupSettings.set({showDraft})}
							data-flx="user.notifications-tab.phone.popups-draft"
						/>
						<SwitchRow
							icon={<KeyboardIcon size={20} />}
							title={i18n._(WAIT_TYPING_DESCRIPTOR)}
							description={i18n._(WAIT_TYPING_HINT_DESCRIPTOR)}
							value={InAppPopupSettings.waitWhileTyping}
							onChange={(waitWhileTyping) => InAppPopupSettings.set({waitWhileTyping})}
							data-flx="user.notifications-tab.phone.popups-typing"
						/>
						<SwitchRow
							icon={<PhoneIcon size={20} />}
							title={i18n._(QUIET_CALLS_DESCRIPTOR)}
							description={i18n._(QUIET_CALLS_HINT_DESCRIPTOR)}
							value={InAppPopupSettings.quietInCalls}
							onChange={(quietInCalls) => InAppPopupSettings.set({quietInCalls})}
							data-flx="user.notifications-tab.phone.popups-calls"
						/>
					</div>
				</SettingsSection>
			)}
		</>
	);
});

const SoundsPage: React.FC = observer(() => {
	const sound = useSoundSettings();
	const soundProps = {
		soundSettings: Sound.settings,
		soundTypeLabels: sound.soundTypeLabels,
		customSounds: sound.customSounds,
		isSoundEnabled: Sound.isSoundTypeEnabled,
		onToggleAllSounds: sound.handleToggleAllSounds,
		onToggleSound: sound.handleToggleSound,
		onPreviewSound: sound.handlePreviewSound,
		onUploadClick: sound.handleUploadClick,
		onCustomSoundDelete: sound.handleCustomSoundDelete,
		onMasterVolumeChange: sound.handleMasterVolumeChange,
		onSoundOverrideChange: sound.handleSoundOverrideChange,
		onSoundOverrideReset: sound.handleSoundOverrideReset,
		onAllOverridesReset: sound.handleAllOverridesReset,
	};
	return (
		<div>
			<PhoneSoundLevels
				soundSettings={soundProps.soundSettings}
				onMasterVolumeChange={soundProps.onMasterVolumeChange}
				onToggleAllSounds={soundProps.onToggleAllSounds}
			/>
			<PhoneMessageSounds {...soundProps} />
		</div>
	);
});

const ReadAloudPage: React.FC = () => {
	const {i18n} = useLingui();
	return (
		<>
			<div className={styles.card}>
				<TextToSpeechPlaybackRow />
			</div>
			<SettingsSection
				id="tts-narration"
				linkable={false}
				title={i18n._(READ_AUTOMATICALLY_DESCRIPTOR)}
				className={styles.bare}
			>
				<TextToSpeechNarrationCards />
			</SettingsSection>
		</>
	);
};

const DeskPage: React.FC<{onAfkTimeoutChange: (minutes: number) => void}> = observer(({onAfkTimeoutChange}) => {
	const {i18n} = useLingui();
	const pushRightAway = UserSettings.afkTimeout === 0;
	const idleMinutes = Math.max(1, Math.min(10, Math.round(UserSettings.afkTimeout / 60)));
	const chips = IDLE_CHIPS.includes(idleMinutes) ? IDLE_CHIPS : [...IDLE_CHIPS, idleMinutes].sort((a, b) => a - b);
	return (
		<div className={styles.card} data-flx="user.notifications-tab.phone.general">
			<SwitchRow
				icon={<DesktopIcon size={20} />}
				title={i18n._(PUSH_WHILE_ON_DESKTOP_DESCRIPTOR)}
				description={i18n._(pushRightAway ? PUSH_RIGHT_AWAY_DESCRIPTOR : PUSH_AFTER_IDLE_DESCRIPTOR)}
				value={pushRightAway}
				onChange={(enabled) => onAfkTimeoutChange(enabled ? 0 : 10)}
				data-flx="user.notifications-tab.phone.push-while-active"
			/>
			{!pushRightAway && (
				<div className={styles.chips} role="radiogroup" aria-label={i18n._(PUSH_AFTER_IDLE_DESCRIPTOR)}>
					{chips.map((minutes) => (
						<button
							key={minutes}
							type="button"
							role="radio"
							aria-checked={minutes === idleMinutes}
							className={clsx(styles.chip, minutes === idleMinutes && styles.chipOn)}
							onClick={() => onAfkTimeoutChange(minutes)}
							data-flx="user.notifications-tab.phone.idle-chip"
						>
							{i18n._(IDLE_MINUTES_DESCRIPTOR, {minutes})}
						</button>
					))}
				</div>
			)}
		</div>
	);
});

const DeviceCard: React.FC<{
	isPwa: boolean;
	loading: boolean;
	subscriptions: ReadonlyArray<{subscription_id: string; user_agent: string | null}>;
	onRegister: () => void;
	onForget: () => void;
}> = ({isPwa, loading, subscriptions, onRegister, onForget}) => {
	const {i18n} = useLingui();
	const here = thisDevice();
	const name = [here.device, here.browser].filter(Boolean).join(' · ') || i18n._(UNKNOWN_DEVICE_DESCRIPTOR);
	const active = subscriptions.length > 0;
	const system = [here.os, isPwa ? i18n._(INSTALLED_APP_DESCRIPTOR) : null].filter(Boolean).join(' · ');
	return (
		<div className={styles.card} data-flx="user.notifications-tab.phone.device">
			<div className={styles.deviceTop}>
				<span className={styles.tile} aria-hidden="true">
					<DeviceMobileIcon size={20} />
				</span>
				<span className={styles.text}>
					<span className={styles.deviceTitle}>{name}</span>
					<span className={styles.status}>
						<i className={clsx(styles.dot, active && styles.dotOk)} />
						{i18n._(loading ? CHECKING_DESCRIPTOR : active ? PUSH_ACTIVE_DESCRIPTOR : PUSH_NOT_SET_UP_DESCRIPTOR)}
					</span>
				</span>
				{active ? (
					<>
						<Tooltip text={i18n._(REFRESH_PUSH_DESCRIPTOR)}>
							<button
								type="button"
								className={styles.iconButton}
								onClick={onRegister}
								aria-label={i18n._(REFRESH_PUSH_DESCRIPTOR)}
								data-flx="user.notifications-tab.phone.device-refresh"
							>
								<ArrowsClockwiseIcon size={18} />
							</button>
						</Tooltip>
						<Tooltip text={i18n._(FORGET_SUBSCRIPTIONS_DESCRIPTOR)}>
							<button
								type="button"
								className={styles.iconButton}
								onClick={onForget}
								aria-label={i18n._(FORGET_SUBSCRIPTIONS_DESCRIPTOR)}
								data-flx="user.notifications-tab.phone.device-forget"
							>
								<TrashIcon size={18} />
							</button>
						</Tooltip>
					</>
				) : (
					<button
						type="button"
						className={styles.pillButton}
						onClick={onRegister}
						disabled={loading}
						data-flx="user.notifications-tab.phone.device-enable"
					>
						{i18n._(ENABLE_PUSH_DESCRIPTOR)}
					</button>
				)}
			</div>
			{(system || active) && (
				<dl className={styles.meta}>
					{system ? (
						<>
							<dt>{i18n._(SYSTEM_DESCRIPTOR)}</dt>
							<dd>{system}</dd>
						</>
					) : null}
					{subscriptions.map((subscription) => {
						const summary = summariseUserAgent(subscription.user_agent);
						const label =
							[summary.device, summary.browser].filter(Boolean).join(' · ') || i18n._(UNKNOWN_DEVICE_DESCRIPTOR);
						return (
							<Fragment key={subscription.subscription_id}>
								<dt>{label}</dt>
								<dd title={subscription.subscription_id}>{shortSubscriptionId(subscription.subscription_id)}</dd>
							</Fragment>
						);
					})}
				</dl>
			)}
		</div>
	);
};

export const PhoneNotificationsTab: React.FC = observer(() => {
	const notifications = useNotificationSettings();
	const page = PhoneNotificationsPage.page;
	const {setContentKey} = useSettingsContentKey();
	useEffect(() => {
		setContentKey(page ? `notifications-${page}` : null);
	}, [page, setContentKey]);
	return (
		<SettingsTabContainer data-flx="user.notifications-tab.phone.container">
			<SettingsTabContent data-flx="user.notifications-tab.phone.content">
				{page === null && <RootPage notifications={notifications} />}
				{page === 'agents' && <PhoneAgentsPage />}
				{page === 'popups' && <PopupsPage />}
				{page === 'sounds' && <SoundsPage />}
				{page === 'read-aloud' && <ReadAloudPage />}
				{page === 'desk' && <DeskPage onAfkTimeoutChange={(minutes) => void notifications.changeAfkTimeout(minutes)} />}
				{page === 'mention' && <MentionPreferenceTabContent />}
				{page === 'device' && (
					<DeviceCard
						isPwa={notifications.isPwa}
						loading={notifications.subscriptionsLoading}
						subscriptions={notifications.subscriptions}
						onRegister={notifications.registerPushSubscription}
						onForget={() => void notifications.forgetPushSubscriptions()}
					/>
				)}
			</SettingsTabContent>
		</SettingsTabContainer>
	);
});
